/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import OnboardingModal from '@/components/OnboardingModal';
import { TopBar } from '@/components/layout/shell/TopBar';
import { SideNav } from '@/components/layout/shell/SideNav';
import { BottomNav } from '@/components/layout/shell/BottomNav';

/**
 * AppShell — authenticated layout: top bar, desktop sidebar, animated outlet,
 * mobile bottom navigation.
 *
 * Refactor note (Code Quality): navigation config, top-bar controls, user menu,
 * side/bottom navs live in `components/layout/shell/*`.
 */
const AppShell = () => {
  const location = useLocation();
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      {/* Skip to main content — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="skip-to-content"
        data-testid="skip-to-content-link"
      >
        {t('a11ySkipToContent', 'Salta al contenuto principale')}
      </a>
      <TopBar />
      <div className="max-w-[1100px] mx-auto px-3 md:px-4 lg:px-6 pb-24 lg:pb-8 pt-4">
        <div className="flex gap-6">
          <SideNav />
          <main
            id="main-content"
            role="main"
            tabIndex={-1}
            className="flex-1 min-w-0 focus:outline-none"
            data-testid="main-content"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
      <BottomNav />
      <OnboardingModal />
    </div>
  );
};

export default AppShell;
